import { ContactDirectory } from '@/services/contact-directory.service';

const initialState = () => ({
  searchResults: [],
  pagination: {
    page: 1,
    nextPage: null,
    previousPage: null,
    totalCount: 0,
    totalPages: 0,
  },
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
  // On phones the results grow page by page instead of showing one page at a time.
  loadingMore: false,
  loadMoreError: null,
});

const state = initialState();

// The searches aren't cancelled: a page that answers after a newer search started belongs
// to the old results and is dropped.
let searchId = 0;

const actions = {
  searchContactDirectory({ commit }, { keywords, page = 1, pageSize = 25, entityType = null }) {
    searchId += 1;
    commit('SEARCH_CONTACT_DIRECTORY');
    return ContactDirectory.searchContactDirectory({ keywords, page, pageSize, entityType })
      .then(({ results, pagination }) => {
        commit('SUCCESS', { results, pagination });
      })
      .catch((error) => {
        commit('ERROR', error);
        throw error;
      });
  },
  // Adds the next page under the listed results. Not `pending`: the list stays usable while
  // it loads, and a failure keeps what's listed.
  loadMoreContactDirectory({ commit }, { keywords, page, pageSize = 25 }) {
    const search = searchId;
    commit('LOAD_MORE');
    return ContactDirectory.searchContactDirectory({ keywords, page, pageSize }).then(
      ({ results, pagination }) => {
        if (search === searchId) commit('APPEND', { results, pagination });
        else commit('LOAD_MORE_CANCELLED');
      },
      (error) => commit(search === searchId ? 'LOAD_MORE_ERROR' : 'LOAD_MORE_CANCELLED', error),
    );
  },
};

const mutations = {
  SUCCESS(state, { results, pagination }) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.searchResults = results;
    state.pagination = pagination;
  },
  SEARCH_CONTACT_DIRECTORY(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.loadingMore = false;
    state.loadMoreError = null;
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error,
    };
  },
  LOAD_MORE(state) {
    state.loadingMore = true;
    state.loadMoreError = null;
  },
  APPEND(state, { results, pagination }) {
    state.searchResults = [...state.searchResults, ...results];
    state.pagination = pagination;
    state.loadingMore = false;
  },
  LOAD_MORE_ERROR(state, error) {
    state.loadingMore = false;
    state.loadMoreError = error;
  },
  LOAD_MORE_CANCELLED(state) {
    state.loadingMore = false;
  },
};

const getters = {
  searchResults: (state) => state.searchResults,
  contactResults: (state) => state.searchResults.filter((result) => result.contactPoint),

  // Pagination
  page: (state) => state.pagination.page,
  totalCount: (state) => state.pagination.totalCount,
  totalPages: (state) => state.pagination.totalPages,
  nextPage: (state) => new URLSearchParams(state.pagination.nextPage).get('page'),
  previousPage: (state) => new URLSearchParams(state.pagination.previousPage).get('page'),

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
  loadingMore: (state) => state.loadingMore,
  loadMoreError: (state) => state.loadMoreError,
};

export const contactDirectory = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
